import { gql, useQuery } from '@apollo/client'

import {
  ArtistFavoriteMembersForFansQuery,
  ArtistFavoriteMembersForFansQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ favoriteMemberId: string }>()
  const favoriteMemberId = router.query.favoriteMemberId

  const { data, loading, fetchMore } = useQuery<
    ArtistFavoriteMembersForFansQuery,
    ArtistFavoriteMembersForFansQueryVariables
  >(ARTIST_FAVORITE_MEMBERS_FOR_FANS_QUERY, {
    variables: {
      inputs: {
        favoriteMemberId: favoriteMemberId || '',
      },
      pageInput: {
        limit: 10,
      },
    },
    skip: !user?.uid || !favoriteMemberId,
  })

  const rawFavoriteMember = data?.artistFavoriteMembers?.[0]

  const fans: State['favoriteMember']['fans'] = (
    rawFavoriteMember?.fans?.edges ?? []
  )
    .map(edge => edge.node)
    .map(fan => ({
      id: fan.user.id,
      displayName: fan.user.displayName ?? '',
      avatar: { uri: fan.user.avatar?.uri ?? '' },
      formattedCreatedAt: fan.formattedCreatedAt ?? '',
    }))

  const pageInfo = rawFavoriteMember?.fans?.pageInfo

  return {
    state: {
      artistId: user?.uid ?? '',
      loading,
      hasNextPage: pageInfo?.hasNextPage ?? false,
      favoriteMember: {
        id: rawFavoriteMember?.id ?? '',
        name: rawFavoriteMember?.name ?? '',
        avatar: rawFavoriteMember?.avatar?.uri ?? '',
        fans,
      },
    },
    actions: {
      async onFetchMore() {
        if (!pageInfo?.hasNextPage) {
          return
        }

        await fetchMore({
          variables: {
            inputs: {
              favoriteMemberId: favoriteMemberId || '',
            },
            pageInput: {
              cursor: pageInfo.endCursor,
              limit: 10,
            },
          },
        })
      },
    },
  }
})

const ARTIST_FAVORITE_MEMBERS_FOR_FANS_QUERY = gql`
  query ArtistFavoriteMembersForFans(
    $pageInput: PageInput
    $inputs: ArtistFavoriteMembersInput!
  ) {
    artistFavoriteMembers(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
      orderNumber
      fans(pageInput: $pageInput) {
        edges {
          node {
            ... on MyFavoriteMember {
              id
              createdAt
              formattedCreatedAt @client
              user {
                id
                displayName
                avatar {
                  uri
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`
